'use client';

import { useEffect, useState } from 'react';
import { isAdminRole } from '@/lib/auth/roles';
import { settings } from '../../config/settings';
import MaintenancePage from './MaintenancePage';
import { apiGetMe } from '../helpers/auth';

// `initial` is the maintenance status read on the server by the root layout.
// When maintenance is off (the normal case) children render straight away, so
// page content is in the server HTML (crawlers, no layout shift). Only while
// maintenance is on do we hold rendering until the role check below finishes.
export default function MaintenanceWrapper({ children, initial = null }) {
  const knownOff = initial ? !initial.enabled : false;
  const [loading, setLoading] = useState(!knownOff);
  const [showMaintenance, setShowMaintenance] = useState(false);
  const [maintenance, setMaintenance] = useState(initial);

  useEffect(() => {
    if (knownOff) return;
    async function checkAccess() {
      // Exclude login page from maintenance mode
      if (typeof window !== 'undefined' && window.location.pathname === '/login') {
        setShowMaintenance(false);
        setLoading(false);
        return;
      }

      // Maintenance mode is toggled from the admin panel (Admin > Maintenance)
      // and stored in the database, falling back to the static config value
      // if the API can't be reached for some reason.
      let enabled = settings.maintenance_mode?.enabled || false;
      let liveMaintenance = null;
      try {
        const res = await fetch('/api/public/maintenance', { cache: 'no-store' });
        if (res.ok) {
          const data = await res.json();
          enabled = Boolean(data.enabled);
          liveMaintenance = data;
        }
      } catch (err) {
        // Fall back to the static config value above
      }
      setMaintenance(liveMaintenance);

      if (!enabled) {
        setShowMaintenance(false);
        setLoading(false);
        return;
      }

      // Check if user has an allowed role
      try {
        const userData = await apiGetMe();
        if (userData.ok && userData.user) {
          const userRole = (userData.user.access_role ?? userData.user.role)?.toLowerCase();
          const allowedRoles = settings.maintenance_mode?.allowed_roles || ['staff', 'admin', 'owner'];

          // Admin-level roles (Developer and up) always get through, plus any extra roles
          // listed in maintenance_mode.allowed_roles.
          if (isAdminRole(userRole) || allowedRoles.map(r => r.toLowerCase()).includes(userRole)) {
            setShowMaintenance(false);
            setLoading(false);
            return;
          }
        }
      } catch (err) {
        // User not logged in or error - show maintenance
      }

      setShowMaintenance(true);
      setLoading(false);
    }

    checkAccess();
  }, [knownOff]);

  if (loading) {
    return null; // Or a loading spinner
  }

  if (showMaintenance) {
    return (
      <MaintenancePage
        description={maintenance?.description}
        startAt={maintenance?.startAt}
        endAt={maintenance?.endAt}
      />
    );
  }

  return children;
}

